import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import SignaturePadLib, { Options as SignaturePadOptions } from 'signature_pad';

export type SignatureMode = 'draw' | 'type';

@Component({
    selector: 'signature-pad',
    template: `
      <div class="signature-mode-toggle">
        <button type="button"
          class="btn btn-sm"
          [class.v-btn-primary]="mode === 'draw'"
          [class.btn-outline-secondary]="mode !== 'draw'"
          (click)="setMode('draw')">
          <i class="fa fa-pencil me-1"></i>Draw
        </button>
        <button type="button"
          class="btn btn-sm"
          [class.v-btn-primary]="mode === 'type'"
          [class.btn-outline-secondary]="mode !== 'type'"
          (click)="setMode('type')">
          <i class="fa fa-keyboard-o me-1"></i>Type
        </button>
      </div>
      
      <div class="signature-draw-area" [style.display]="mode === 'draw' ? 'block' : 'none'">
        <canvas #signatureCanvas></canvas>
      </div>
      
      @if (mode === 'type') {
        <div class="signature-type-area">
          <input
            #typedInput
            type="text"
            class="signature-type-input"
            [placeholder]="typePlaceholder"
            [value]="typedName"
            (input)="onTypedInput($event)"
            maxlength="100" />
          @if (typedName) {
            <div class="signature-type-preview">
              <canvas #typedCanvas></canvas>
            </div>
          }
        </div>
      }
      `,
    styles: [
      `
      :host {
        display: block;
      }
      .signature-mode-toggle {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
      }
      .signature-draw-area {
        border: 1px solid #ccc;
        border-radius: 4px;
        background: #fff;
        position: relative;
        width: 100%;
        min-height: 200px;
      }
      .signature-draw-area canvas {
        display: block;
        width: 100%;
        height: 200px;
        cursor: crosshair;
      }
      .signature-type-area {
        width: 100%;
      }
      .signature-type-input {
        width: 100%;
        padding: 0.5rem 0.75rem;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        outline: none;
      }
      .signature-type-input:focus {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
      .signature-type-preview {
        margin-top: 0.75rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        background: #fff;
        padding: 1rem;
        text-align: center;
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .signature-type-preview canvas {
        display: block;
        max-width: 100%;
      }
      `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
  @ViewChild('signatureCanvas', { static: false })
  canvasRef: ElementRef<HTMLCanvasElement>;

  @ViewChild('typedCanvas', { static: false })
  typedCanvasRef: ElementRef<HTMLCanvasElement>;

  @Input() options: Partial<SignaturePadOptions> = {};
  @Input() typePlaceholder = 'Type your full name';

  @Output() onBeginEvent = new EventEmitter<void>();
  @Output() onEndEvent = new EventEmitter<void>();

  public mode: SignatureMode = 'draw';
  public typedName = '';

  private signaturePad: SignaturePadLib;
  private drawInitialized = false;
  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    this.initDrawPad();
    this.observeCanvasSize();
  }

  ngOnDestroy(): void {
    if (this.signaturePad) {
      this.signaturePad.off();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.mode === 'draw' && this.signaturePad) {
      this.resizeCanvas();
    }
  }

  public setMode(newMode: SignatureMode): void {
    if (newMode === this.mode) return;
    this.mode = newMode;

    if (newMode === 'draw') {
      // Re-initialize canvas after it becomes visible
      setTimeout(() => {
        if (!this.drawInitialized) {
          this.initDrawPad();
        } else {
          this.resizeCanvas();
        }
      });
    } else if (newMode === 'type' && this.typedName) {
      setTimeout(() => this.renderTypedSignature());
    }
  }

  public toDataURL(type?: string, encoderOptions?: number): string {
    if (this.mode === 'type') {
      return this.getTypedDataURL();
    }
    return this.signaturePad.toDataURL(type, encoderOptions);
  }

  public clear(): void {
    if (this.mode === 'draw' && this.signaturePad) {
      this.signaturePad.clear();
    }
    this.typedName = '';
  }

  public isEmpty(): boolean {
    if (this.mode === 'type') {
      return !this.typedName.trim();
    }
    return this.signaturePad ? this.signaturePad.isEmpty() : true;
  }

  public onTypedInput(event: Event): void {
    this.typedName = (event.target as HTMLInputElement).value;
    this.onBeginEvent.emit();

    setTimeout(() => {
      this.renderTypedSignature();
      this.onEndEvent.emit();
    });
  }

  private initDrawPad(): void {
    if (!this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    this.signaturePad = new SignaturePadLib(canvas, this.options);

    this.signaturePad.addEventListener('beginStroke', () => {
      this.onBeginEvent.emit();
    });

    this.signaturePad.addEventListener('endStroke', () => {
      this.onEndEvent.emit();
    });

    this.resizeCanvas();
    this.drawInitialized = true;
  }

  private observeCanvasSize(): void {
    if (typeof ResizeObserver === 'undefined' || !this.canvasRef) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.mode === 'draw' && this.signaturePad) {
        this.resizeCanvas();
      }
    });
    this.resizeObserver.observe(this.canvasRef.nativeElement);
  }

  private resizeCanvas(): void {
    if (!this.canvasRef || !this.signaturePad) return;

    const canvas = this.canvasRef.nativeElement;
    const data = this.signaturePad.isEmpty()
      ? null
      : this.signaturePad.toData();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    this.signaturePad.clear();

    if (data) {
      this.signaturePad.fromData(data);
    }
  }

  private renderTypedSignature(): void {
    if (!this.typedCanvasRef || !this.typedName.trim()) return;

    const canvas = this.typedCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const fontSize = 40;
    const fontFamily = "'Dancing Script', 'Brush Script MT', 'Segoe Script', cursive";

    // Measure text to size canvas
    ctx.font = `${fontSize}px ${fontFamily}`;
    const metrics = ctx.measureText(this.typedName);
    const textWidth = metrics.width + 40; // padding
    const textHeight = fontSize * 2;

    canvas.width = textWidth * ratio;
    canvas.height = textHeight * ratio;
    canvas.style.width = `${textWidth}px`;
    canvas.style.height = `${textHeight}px`;
    ctx.scale(ratio, ratio);

    // Render signature text
    ctx.clearRect(0, 0, textWidth, textHeight);
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.typedName, textWidth / 2, textHeight / 2);
  }

  private getTypedDataURL(): string {
    if (!this.typedCanvasRef) {
      // Render to an offscreen canvas if the preview isn't visible
      const offscreen = document.createElement('canvas');
      const ctx = offscreen.getContext('2d');
      const fontSize = 40;
      const fontFamily = "'Dancing Script', 'Brush Script MT', 'Segoe Script', cursive";

      ctx.font = `${fontSize}px ${fontFamily}`;
      const metrics = ctx.measureText(this.typedName);
      const w = metrics.width + 40;
      const h = fontSize * 2;

      offscreen.width = w * 2;
      offscreen.height = h * 2;
      ctx.scale(2, 2);

      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.typedName, w / 2, h / 2);

      return offscreen.toDataURL();
    }

    return this.typedCanvasRef.nativeElement.toDataURL();
  }
}
